import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Chip, Tooltip, CircularProgress, Alert, Snackbar, Divider, Accordion,
  AccordionSummary, AccordionDetails, Switch, FormControlLabel, Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';
import { MODALITY_CONFIG } from '../models/imagingModel';

const { primary: IMG_PRIMARY, teal: IMG_TEAL } = COLORS;

const CATEGORIES = ['Radiología', 'Ecografía', 'TC', 'RMN', 'Dental', 'Endoscopía', 'Otro'];
const SPECIES = ['canino', 'felino', 'equino', 'bovino', 'otro'];
const MODALITIES = ['RX', 'ECO', 'TC', 'MRI', 'DENTAL', 'ENDOSCOPIA', 'OTRO'];
const BODY_REGIONS = ['Tórax', 'Abdomen', 'Columna', 'Extremidades', 'Cabeza', 'Cuello', 'Pelvis', 'Otro'];
const DEFAULT_VIEWS = ['AP', 'Lateral', 'Oblicua', 'VD', 'Dorsoventral', 'Otro'];

const emptyExam = () => ({
  name: '',
  shortName: '',
  category: 'Radiología',
  modality: 'RX',
  defaultViews: [],
  defaultBodyRegion: '',
  sedationTypical: false,
  contrastTypical: false,
  estimatedDurationMin: 30,
  turnaroundTargetHours: 24,
  species: ['canino', 'felino'],
  arancelId: '',
  arancelPrestacion: '',
  reportTemplate: '',
  isActive: true,
});

export default function ImagingExamCatalog() {
  const { filterByBranch } = useBranch();
  const [examTypes, setExamTypes] = useState([]);
  const [aranceles, setAranceles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, exam: null });
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadExams = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'imagingExamTypes'), orderBy('category')));
      setExamTypes(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    } catch (err) {
      console.error('Error loading exams:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExams();
    // Cargar aranceles filtrados por unidad de negocio "Exámenes"
    const loadAranceles = async () => {
      try {
        const unitSnap = await getDocs(query(collection(db, 'unidadesNegocio'), orderBy('name')));
        const examUnit = unitSnap.docs.find(d => d.data().name === 'Exámenes');
        if (examUnit) {
          const arancelesSnap = await getDocs(query(
            collection(db, 'aranceles'),
            where('unidadNegocioId', '==', examUnit.id)
          ));
          const list = filterByBranch(arancelesSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((a) => a.activo !== false), { includeUnassigned: true })
            .sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es'));
          setAranceles(list);
        }
      } catch (err) {
        console.error('Error loading aranceles:', err);
        // Fallback: cargar todos los aranceles activos
        try {
          const allSnap = await getDocs(query(collection(db, 'aranceles')));
          const list = filterByBranch(allSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((a) => a.activo !== false), { includeUnassigned: true })
            .sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es'));
          setAranceles(list);
        } catch (fallbackErr) {
          console.error('Error loading aranceles fallback:', fallbackErr);
        }
      }
    };
    loadAranceles();
  }, []);

  const openNew = () => setDialog({ open: true, exam: emptyExam() });
  const openEdit = (exam) => setDialog({
    open: true,
    exam: {
      ...exam,
      defaultViews: exam.defaultViews || [],
      species: exam.species || ['canino', 'felino'],
      reportTemplate: exam.reportTemplate || '',
      arancelId: exam.arancelId || '',
      arancelPrestacion: exam.arancelPrestacion || '',
    },
  });

  const closeDialog = () => setDialog({ open: false, exam: null });

  const setExamField = (field, value) =>
    setDialog((d) => ({ ...d, exam: { ...d.exam, [field]: value } }));

  const handleSave = async () => {
    const { exam } = dialog;
    if (!exam.name || !exam.modality) {
      setSnackbar({ open: true, message: 'Nombre y modalidad son requeridos', severity: 'warning' });
      return;
    }

    setSaving(true);
    try {
      if (exam.id) {
        await updateDoc(doc(db, 'imagingExamTypes', exam.id), {
          ...exam,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'imagingExamTypes'), {
          ...exam,
          createdAt: serverTimestamp(),
        });
      }
      setSnackbar({ open: true, message: 'Estudio guardado exitosamente', severity: 'success' });
      closeDialog();
      loadExams();
    } catch (err) {
      console.error('Error saving exam:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (examId) => {
    if (!window.confirm('¿Estás seguro de eliminar este estudio?')) return;
    try {
      await deleteDoc(doc(db, 'imagingExamTypes', examId));
      setSnackbar({ open: true, message: 'Estudio eliminado', severity: 'success' });
      loadExams();
    } catch (err) {
      console.error('Error deleting exam:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  const toggleActive = async (exam) => {
    try {
      await updateDoc(doc(db, 'imagingExamTypes', exam.id), {
        isActive: !exam.isActive,
      });
      loadExams();
    } catch (err) {
      console.error('Error toggling active:', err);
    }
  };

  // Group by modality
  const groupedExams = examTypes.reduce((acc, exam) => {
    const modality = exam.modality || 'OTRO';
    if (!acc[modality]) acc[modality] = [];
    acc[modality].push(exam);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BrokenImageIcon sx={{ color: IMG_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>Catálogo de Estudios de Imagen</Typography>
            <Typography variant="caption" color="text.secondary">Gestión de tipos de estudios disponibles</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}
          sx={{ bgcolor: IMG_TEAL, '&:hover': { bgcolor: '#229984' }, fontWeight: 600 }}>
          Nuevo Estudio
        </Button>
      </Box>

      {/* Accordion by modality */}
      {Object.entries(MODALITY_CONFIG).map(([modality, config]) => {
        const exams = groupedExams[modality] || [];
        if (exams.length === 0) return null;

        return (
          <Accordion key={modality} defaultExpanded={modality === 'RX'} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: config.bg, borderRadius: '8px 8px 0 0 !important' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={config.label} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: config.color, fontWeight: 600 }} />
                <Typography variant="subtitle1" fontWeight={700} color={config.color}>{exams.length} estudio(s)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#FFFFFF' }}>
              <Grid container spacing={2}>
                {exams.map((exam) => (
                  <Grid key={exam.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0', position: 'relative' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="body1" fontWeight={600}>{exam.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => openEdit(exam)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDelete(exam.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {exam.shortName || '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                        <Chip label={exam.category} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
                        <Chip label={`${exam.estimatedDurationMin || 30} min`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
                        <Chip label={`TAT ${exam.turnaroundTargetHours || 24}h`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {exam.arancelPrestacion || 'Sin arancel'}
                        </Typography>
                        <FormControlLabel
                          control={<Switch size="small" checked={exam.isActive} onChange={() => toggleActive(exam)} />}
                          label={<Typography variant="caption">{exam.isActive ? 'Activo' : 'Inactivo'}</Typography>}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Edit/Add Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialog.exam?.id ? 'Editar Estudio' : 'Nuevo Estudio'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre del estudio *"
                value={dialog.exam?.name || ''}
                onChange={(e) => setExamField('name', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nombre corto"
                value={dialog.exam?.shortName || ''}
                onChange={(e) => setExamField('shortName', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Categoría"
                value={dialog.exam?.category || ''}
                onChange={(e) => setExamField('category', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {CATEGORIES.map((cat) => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Modalidad *"
                value={dialog.exam?.modality || ''}
                onChange={(e) => setExamField('modality', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {MODALITIES.map((mod) => <MenuItem key={mod} value={mod}>{mod}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Región anatómica default"
                value={dialog.exam?.defaultBodyRegion || ''}
                onChange={(e) => setExamField('defaultBodyRegion', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {BODY_REGIONS.map((region) => <MenuItem key={region} value={region}>{region}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Vistas default (separadas por coma)"
                value={dialog.exam?.defaultViews?.join(', ') || ''}
                onChange={(e) => setExamField('defaultViews', e.target.value.split(',').map(v => v.trim()))}
                size="small"
                placeholder="AP, Lateral"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Duración estimada (min)"
                value={dialog.exam?.estimatedDurationMin || 30}
                onChange={(e) => setExamField('estimatedDurationMin', parseInt(e.target.value) || 30)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="TAT objetivo (horas)"
                value={dialog.exam?.turnaroundTargetHours || 24}
                onChange={(e) => setExamField('turnaroundTargetHours', parseInt(e.target.value) || 24)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Autocomplete
                options={aranceles}
                getOptionLabel={(a) => a.prestacion || ''}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={dialog.exam?.arancelId ? aranceles.find((a) => a.id === dialog.exam.arancelId) || null : null}
                onChange={(_, selected) => {
                  setDialog((d) => ({
                    ...d,
                    exam: {
                      ...d.exam,
                      arancelId: selected?.id || '',
                      arancelPrestacion: selected?.prestacion || '',
                    },
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Vincular Arancel"
                    placeholder="Buscar prestación..."
                    helperText={aranceles.length === 0 ? 'No hay aranceles de exámenes configurados' : ''} />
                )}
                noOptionsText="Sin resultados"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" fontWeight={700} display="block" mb={1}>Especies aplicables</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {SPECIES.map((sp) => (
                  <Chip
                    key={sp}
                    label={sp}
                    size="small"
                    clickable
                    onClick={() => {
                      const current = dialog.exam?.species || [];
                      const updated = current.includes(sp)
                        ? current.filter(s => s !== sp)
                        : [...current, sp];
                      setExamField('species', updated);
                    }}
                    sx={{
                      bgcolor: (dialog.exam?.species || []).includes(sp) ? IMG_PRIMARY : '#F1F5F9',
                      color: (dialog.exam?.species || []).includes(sp) ? 'white' : '#64748B',
                    }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dialog.exam?.sedationTypical || false}
                      onChange={(e) => setExamField('sedationTypical', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Sedación típica</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={dialog.exam?.contrastTypical || false}
                      onChange={(e) => setExamField('contrastTypical', e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Contraste típico</Typography>}
                />
              </Box>
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Template de informe (opcional)"
                value={dialog.exam?.reportTemplate || ''}
                onChange={(e) => setExamField('reportTemplate', e.target.value)}
                helperText="Texto pre-cargado para el informe de este estudio"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} startIcon={saving ? <CircularProgress size={20} /> : null}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
